import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ThreeEvent, useFrame, useThree } from "@react-three/fiber";
import { observer } from "mobx-react-lite";
import * as THREE from "three";
import { OrbitControls as OrbitControlsImpl } from "three-stdlib";
import {
  FloorPlan3DPreset,
  MIN_INTERACTIVE_ZOOM_LEVEL,
  editorStoreContext,
} from "../../../store/editorStore";
import { markPanningDragFinished } from "../../../utils/panningGesture";
import { DEFAULT_WALL_HEIGHT } from "../geometry";
import {
  BASE_PLANE_Z,
  getCameraTargetOnZPlane,
  getSynchronizedBasePlaneOrbitTarget,
} from "./cameraTarget";
import { isPointerOverRegisteredTransformControlsPicker } from "../interaction/symbol3DRotationGizmo";
import {
  TWO_D_CAMERA_FOV_DEG,
  TWO_D_CAMERA_Z,
} from "../../../utils/floorPlan2DCamera";
import { isMobileUi } from "../../../utils/mobileUi";

const TRANSITION_MS = 260;
const CAMERA_NEAR = 0.05;
const CAMERA_FAR = 3000;
const BASE_PAN_HIT_PLANE_Z = -0.12;
const BASE_PAN_MIN_HIT_SIZE = 450;
const PAN_DRAG_THRESHOLD_PX = 2;
const THREE_D_ZOOM_STEP_FACTOR = 1.12;
const WHEEL_DELTA_PER_ZOOM_STEP = 100;
const MAX_WHEEL_ZOOM_STEPS = 4;
const THREE_D_CAMERA_ZOOM = 1;
const BIRDS_EYE_ENTRY_TILT = THREE.MathUtils.degToRad(2);
const MIN_3D_ZOOM_IN_DISTANCE = 1.5;
const MIN_3D_ZOOM_OUT_DISTANCE = 12;
const RESET_ZOOM_FIT_PADDING = 1.18;
const RESET_ZOOM_TARGET_Z = 0.85;
const BASE_PAN_PLANE = new THREE.Plane(
  new THREE.Vector3(0, 0, 1),
  -BASE_PLANE_Z,
);

type CameraSnapshot = {
  camera: THREE.Camera;
  position: THREE.Vector3;
  quaternion: THREE.Quaternion;
  up: THREE.Vector3;
  zoom?: number;
  fov?: number;
};

export type PresetPose = {
  position: THREE.Vector3;
  target: THREE.Vector3;
  up?: THREE.Vector3;
};

type BasePlanePanState = {
  pointerId: number;
  anchor: THREE.Vector3;
  camera: THREE.PerspectiveCamera;
  cameraPosition: THREE.Vector3;
  orbitTarget: THREE.Vector3;
  hasDragged: boolean;
  startClientX: number;
  startClientY: number;
  clearSelectionOnClick: boolean;
};

type BasePlanePanPointerEvent = Pick<
  PointerEvent,
  "clientX" | "clientY" | "pointerId"
>;

type BasePlanePanNativeListeners = {
  pointerMove: (event: PointerEvent) => void;
  pointerUp: (event: PointerEvent) => void;
  pointerCancel: (event: PointerEvent) => void;
};

const easeInOut = (t: number): number => 0.5 - Math.cos(Math.PI * t) / 2;

const FloorPlan3DCameraController: React.FC<{
  active: boolean;
  planBounds: THREE.Box2;
  controlsEnabled: boolean;
  suppressBasePanHitTarget?: boolean;
  entry2DView?: {
    x: number;
    y: number;
    z: number;
    zoom: number;
  } | null;
  onExit2DView?: (view: {
    x: number;
    y: number;
    z: number;
    zoom: number;
  }) => void;
  onExitComplete?: () => void;
}> = observer(
  ({
    active,
    planBounds,
    controlsEnabled,
    suppressBasePanHitTarget = false,
    entry2DView,
    onExit2DView,
    onExitComplete,
  }) => {
    const editorStore = React.useContext(editorStoreContext);
    const { camera, events, get, gl, set, size, invalidate } = useThree();
    const controlsRef = useRef<OrbitControlsImpl | null>(null);
    const camera3DRef = useRef<THREE.PerspectiveCamera | undefined>(undefined);
    const savedCameraRef = useRef<CameraSnapshot | null>(null);
    const activeRef = useRef(false);
    const animationRef = useRef<number>(0);
    const entryRevealActiveRef = useRef(false);
    const controlsReadyTimeoutRef = useRef<number | null>(null);
    const basePlanePanStateRef = useRef<BasePlanePanState | null>(null);
    const appliedPresetRef = useRef<FloorPlan3DPreset | null>(null);
    const entryOrbitTargetRef = useRef<THREE.Vector3 | null>(null);
    const entry3DViewDistanceRef = useRef<number | null>(null);
    const pendingExitSnapshotRef = useRef<CameraSnapshot | null>(null);
    const userInteracted3DRef = useRef(false);
    const controlsInteractionActiveRef = useRef(false);
    const controlsEnablementRef = useRef({
      active: false,
      controlsReady: false,
      controlsEnabled: false,
      basePlanePanning: false,
      panning: false,
    });
    const handled3DPresetRequestIdRef = useRef(0);
    const handled3DZoomRequestIdRef = useRef(0);
    const handled3DResetZoomRequestIdRef = useRef(0);
    const basePlanePanNativeListenersRef =
      useRef<BasePlanePanNativeListeners | null>(null);
    const [controlsReady, setControlsReady] = useState(false);
    const [basePlanePanning, setBasePlanePanning] = useState(false);

    if (!camera3DRef.current) {
      camera3DRef.current = new THREE.PerspectiveCamera(
        45,
        size.width / Math.max(size.height, 1),
        CAMERA_NEAR,
        CAMERA_FAR,
      );
      camera3DRef.current.up.set(0, 0, 1);
      camera3DRef.current.zoom = THREE_D_CAMERA_ZOOM;
    }

    const controls = useMemo(() => {
      const nextControls = new OrbitControlsImpl(camera3DRef.current!);
      nextControls.enableDamping = false;
      controlsRef.current = nextControls;
      return nextControls;
    }, []);

    const cameraTarget = useMemo(() => {
      const center = planBounds.isEmpty()
        ? new THREE.Vector2(0, 0)
        : planBounds.getCenter(new THREE.Vector2());
      return new THREE.Vector3(center.x, center.y, 0.85);
    }, [planBounds]);
    const [orbitTarget, setOrbitTarget] = useState(() => cameraTarget.clone());
    const orbitTargetRef = useRef(orbitTarget.clone());

    useEffect(() => {
      orbitTargetRef.current.copy(orbitTarget);
    }, [orbitTarget]);

    useEffect(() => {
      controls.target.copy(orbitTarget);
      controls.update();
      invalidate();
    }, [controls, invalidate, orbitTarget]);

    useEffect(() => {
      if (!active) return;
      const previousControls = get().controls;
      set({ controls });
      return () => {
        set({ controls: previousControls });
      };
    }, [active, controls, get, set]);

    useEffect(
      () => () => {
        controls.dispose();
        if (controlsRef.current === controls) {
          controlsRef.current = null;
        }
      },
      [controls],
    );

    useEffect(() => {
      const camera3D = camera3DRef.current;
      if (!camera3D) return;
      camera3D.aspect = size.width / Math.max(size.height, 1);
      camera3D.updateProjectionMatrix();
      invalidate();
    }, [invalidate, size.height, size.width]);

    useEffect(() => {
      const camera3D = camera3DRef.current;
      if (!camera3D) return;

      if (active && !activeRef.current) {
        const entryView = entry2DView ?? editorStore.last2DCameraView;
        const sourceCamera =
          camera === camera3D ? (editorStore.camera ?? camera) : camera;
        const sourceSnapshot = createRemembered2DSnapshot(
          sourceCamera,
          entryView,
        );
        const entryOrbitTarget = createRemembered2DOrbitTarget(entryView);

        savedCameraRef.current = sourceSnapshot;
        entryOrbitTargetRef.current = entryOrbitTarget.clone();
        userInteracted3DRef.current = false;
        copyCameraPoseFor3DEntry(sourceCamera, camera3D);
        apply3DEntryPose(
          camera3D,
          sourceSnapshot,
          entryOrbitTarget,
          editorStore.view3DPreset,
          planBounds,
        );
        camera3D.near = CAMERA_NEAR;
        camera3D.far = CAMERA_FAR;
        camera3D.updateProjectionMatrix();
        handled3DZoomRequestIdRef.current = editorStore.view3DZoomRequestId;
        // Keep a pending focused reset (e.g. newly added room) so it still runs
        // once controls are ready. Otherwise stamp as handled to ignore stale resets.
        if (!editorStore.view3DResetZoomBounds) {
          handled3DResetZoomRequestIdRef.current =
            editorStore.view3DResetZoomRequestId;
        }
        setControlsReady(false);
        if (controlsReadyTimeoutRef.current !== null) {
          window.clearTimeout(controlsReadyTimeoutRef.current);
          controlsReadyTimeoutRef.current = null;
        }
        const synchronizedEntryTarget = getSynchronizedBasePlaneOrbitTarget(
          camera3D,
          entryOrbitTarget,
        );
        setOrbitTarget(synchronizedEntryTarget.clone());
        controls.target.copy(synchronizedEntryTarget);
        orbitTargetRef.current.copy(synchronizedEntryTarget);
        controls.update();
        controls.saveState();
        entry3DViewDistanceRef.current = getSnapshotDistanceToZPlane(
          snapshotCamera(camera3D),
          BASE_PLANE_Z,
        );
        handled3DPresetRequestIdRef.current = editorStore.view3DPresetRequestId;
        set({ camera: camera3D });
        editorStore.setCamera(camera3D);
        activeRef.current = true;
        entryRevealActiveRef.current = true;
        appliedPresetRef.current = editorStore.view3DPreset;
        controlsReadyTimeoutRef.current = window.setTimeout(() => {
          const target = getSynchronizedBasePlaneOrbitTarget(
            camera3D,
            entryOrbitTargetRef.current ??
              create3DEntryOrbitTarget(snapshotCamera(camera3D), BASE_PLANE_Z),
          );
          setOrbitTarget(target.clone());
          controls.target.copy(target);
          orbitTargetRef.current.copy(target);
          controls.update();
          controls.saveState();
          entryRevealActiveRef.current = false;
          setControlsReady(true);
          invalidate();
        }, TRANSITION_MS);
        invalidate();
        return;
      }
    }, [
      active,
      camera,
      cameraTarget,
      editorStore,
      invalidate,
      planBounds,
      set,
    ]);

    useLayoutEffect(() => {
      if (active || !activeRef.current || !savedCameraRef.current) return;
      const camera3D = camera3DRef.current;
      if (!camera3D) return;

      const saved = buildExit2DSnapshot(
        snapshotCamera(camera3D),
        savedCameraRef.current,
        getCurrentOrbitTarget(controlsRef.current, orbitTargetRef.current),
        entry3DViewDistanceRef.current,
        userInteracted3DRef.current,
      );
      pendingExitSnapshotRef.current = saved;
      onExit2DView?.(cameraSnapshotToView(saved));
      setControlsReady(false);
      entryRevealActiveRef.current = false;
      appliedPresetRef.current = null;
      entryOrbitTargetRef.current = null;
      if (controlsReadyTimeoutRef.current !== null) {
        window.clearTimeout(controlsReadyTimeoutRef.current);
        controlsReadyTimeoutRef.current = null;
      }

      restoreCameraSnapshot(saved);
      set({ camera: saved.camera as any });
      editorStore.setCamera(
        saved.camera as THREE.PerspectiveCamera | THREE.OrthographicCamera,
      );
      editorStore.remember2DCameraView(
        saved.camera as THREE.PerspectiveCamera | THREE.OrthographicCamera,
      );
      activeRef.current = false;
      savedCameraRef.current = null;
      entry3DViewDistanceRef.current = null;
      pendingExitSnapshotRef.current = null;
      invalidate();
      onExitComplete?.();
    }, [
      active,
      editorStore,
      invalidate,
      onExit2DView,
      onExitComplete,
      set,
    ]);

    useEffect(() => {
      if (!activeRef.current || !active) return;
      const camera3D = camera3DRef.current;
      if (!camera3D) return;
      if (!controlsReady || entryRevealActiveRef.current) return;
      const presetRequestId = editorStore.view3DPresetRequestId;
      const hasPresetShortcutRequest =
        presetRequestId !== handled3DPresetRequestIdRef.current;
      if (
        !hasPresetShortcutRequest &&
        appliedPresetRef.current === editorStore.view3DPreset
      ) {
        return;
      }
      handled3DPresetRequestIdRef.current = presetRequestId;

      const presetPose = getPresetPose(editorStore.view3DPreset, planBounds);
      const presetDirection = presetPose.position
        .clone()
        .sub(presetPose.target);
      if (presetDirection.lengthSq() < 1e-10) {
        appliedPresetRef.current = editorStore.view3DPreset;
        return;
      }
      presetDirection.normalize();

      // Preserve current pan (orbit target) and zoom (distance) — only tilt the
      // viewing direction so switching presets feels like a camera rotation
      // around what the user is already looking at.
      const currentTarget = orbitTargetRef.current.clone();
      const currentDistance = Math.max(
        camera3D.position.distanceTo(currentTarget),
        1e-3,
      );
      const tiltPose: PresetPose = {
        position: currentTarget
          .clone()
          .add(presetDirection.multiplyScalar(currentDistance)),
        target: currentTarget,
        up: presetPose.up,
      };

      appliedPresetRef.current = editorStore.view3DPreset;
      userInteracted3DRef.current = true;

      animateCamera(camera3D, tiltPose, 240, invalidate, animationRef, () => {
        const controls = controlsRef.current;
        if (controls) {
          controls.target.copy(tiltPose.target);
          orbitTargetRef.current.copy(tiltPose.target);
          controls.update();
        }
        editorStore.setCamera(camera3D);
        invalidate();
      });
    }, [
      active,
      controlsReady,
      editorStore,
      editorStore.view3DPreset,
      editorStore.view3DPresetRequestId,
      invalidate,
      planBounds,
    ]);

    const removeBasePlanePanNativeListeners = useCallback(() => {
      const listeners = basePlanePanNativeListenersRef.current;
      if (!listeners) return;
      window.removeEventListener("pointermove", listeners.pointerMove, true);
      window.removeEventListener("pointerup", listeners.pointerUp, true);
      window.removeEventListener(
        "pointercancel",
        listeners.pointerCancel,
        true,
      );
      basePlanePanNativeListenersRef.current = null;
    }, []);

    const finishBasePlanePan = useCallback(
      (event?: ThreeEvent<PointerEvent> | PointerEvent) => {
        const panState = basePlanePanStateRef.current;
        if (!panState) return;
        if (event && event.pointerId !== panState.pointerId) return;

        const threeEvent = event && "nativeEvent" in event ? event : undefined;
        threeEvent?.stopPropagation();
        const nativeEvent = (threeEvent?.nativeEvent ?? event) as
          | PointerEvent
          | undefined;
        nativeEvent?.preventDefault();
        try {
          const pointerTarget = event?.target as unknown as {
            releasePointerCapture?: (pointerId: number) => void;
          };
          pointerTarget?.releasePointerCapture?.(panState.pointerId);
        } catch {
          // Pointer capture can already be released by the browser on cancel.
        }

        removeBasePlanePanNativeListeners();
        basePlanePanStateRef.current = null;
        setBasePlanePanning(false);
        document.body.style.cursor = "auto";

        const controls = controlsRef.current;
        if (controls) {
          controls.enabled = controlsEnabled && !editorStore.panning;
          orbitTargetRef.current.copy(controls.target);
          setOrbitTarget(controls.target.clone());
        }

        if (panState.hasDragged) {
          markPanningDragFinished();
        } else if (panState.clearSelectionOnClick) {
          editorStore.clearSelections();
        }

        invalidate();
      },
      [
        controlsEnabled,
        editorStore,
        editorStore.panning,
        invalidate,
        removeBasePlanePanNativeListeners,
      ],
    );

    useEffect(
      () => () => {
        if (basePlanePanStateRef.current) {
          removeBasePlanePanNativeListeners();
          basePlanePanStateRef.current = null;
          document.body.style.cursor = "auto";
        }
      },
      [removeBasePlanePanNativeListeners],
    );

    const updateBasePlanePan = useCallback(
      (event: BasePlanePanPointerEvent) => {
        const panState = basePlanePanStateRef.current;
        if (!panState || event.pointerId !== panState.pointerId) return;

        const pointerDeltaX = event.clientX - panState.startClientX;
        const pointerDeltaY = event.clientY - panState.startClientY;
        if (!panState.hasDragged) {
          const movedFarEnough =
            Math.abs(pointerDeltaX) > PAN_DRAG_THRESHOLD_PX ||
            Math.abs(pointerDeltaY) > PAN_DRAG_THRESHOLD_PX;

          if (!movedFarEnough) {
            return;
          }

          panState.hasDragged = true;
        }

        const currentPoint = getPointerPointOnBasePlane(
          event,
          panState.camera,
          gl.domElement,
        );
        const camera3D = camera3DRef.current;
        const controls = controlsRef.current;
        if (!currentPoint || !camera3D || !controls) return;

        const panDelta = panState.anchor.clone().sub(currentPoint);
        if (!isFiniteVector3(panDelta) || panDelta.lengthSq() <= 1e-12) return;

        userInteracted3DRef.current = true;
        camera3D.position.copy(panState.cameraPosition).add(panDelta);
        controls.target.copy(panState.orbitTarget).add(panDelta);
        orbitTargetRef.current.copy(controls.target);
        controls.update();
        camera3D.updateMatrixWorld();
        markPresetSelectionAsPerspective(editorStore, appliedPresetRef);
        invalidate();
      },
      [editorStore, gl.domElement, invalidate],
    );

    const handleBasePlanePointerDown = useCallback(
      (event: ThreeEvent<PointerEvent>) => {
        if (
          !activeRef.current ||
          !controlsEnabled ||
          !editorStore.panning ||
          event.button !== 0
        ) {
          return;
        }
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey)
          return;

        const camera3D = camera3DRef.current;
        const controls = controlsRef.current;
        if (!camera3D || !controls) return;

        const panStartCamera = camera3D.clone();
        panStartCamera.position.copy(camera3D.position);
        panStartCamera.quaternion.copy(camera3D.quaternion);
        panStartCamera.up.copy(camera3D.up);
        panStartCamera.aspect = camera3D.aspect;
        panStartCamera.fov = camera3D.fov;
        panStartCamera.zoom = camera3D.zoom;
        panStartCamera.near = camera3D.near;
        panStartCamera.far = camera3D.far;
        panStartCamera.updateProjectionMatrix();
        panStartCamera.updateMatrixWorld(true);

        const anchor = getPointerPointOnBasePlane(
          event,
          panStartCamera,
          gl.domElement,
        );
        if (!anchor) return;
        const panOrbitTarget =
          getCameraTargetOnZPlane(panStartCamera, BASE_PLANE_Z) ??
          controls.target.clone();

        event.stopPropagation();
        event.nativeEvent.preventDefault();
        event.nativeEvent.stopImmediatePropagation?.();
        const pointerTarget = event.target as unknown as {
          setPointerCapture?: (pointerId: number) => void;
        };
        pointerTarget.setPointerCapture?.(event.pointerId);

        controls.enabled = false;
        controls.target.copy(panOrbitTarget);
        orbitTargetRef.current.copy(panOrbitTarget);
        controls.saveState();

        basePlanePanStateRef.current = {
          pointerId: event.pointerId,
          anchor,
          camera: panStartCamera,
          cameraPosition: camera3D.position.clone(),
          orbitTarget: panOrbitTarget,
          hasDragged: false,
          startClientX: event.clientX,
          startClientY: event.clientY,
          clearSelectionOnClick: shouldClearSelectionOnBasePanClick(event),
        };
        removeBasePlanePanNativeListeners();
        const pointerMove = (nativeEvent: PointerEvent) => {
          if (nativeEvent.pointerId !== event.pointerId) return;
          nativeEvent.preventDefault();
          updateBasePlanePan(nativeEvent);
        };
        const pointerUp = (nativeEvent: PointerEvent) => {
          finishBasePlanePan(nativeEvent);
        };
        const pointerCancel = (nativeEvent: PointerEvent) => {
          finishBasePlanePan(nativeEvent);
        };
        basePlanePanNativeListenersRef.current = {
          pointerMove,
          pointerUp,
          pointerCancel,
        };
        window.addEventListener("pointermove", pointerMove, {
          capture: true,
          passive: false,
        });
        window.addEventListener("pointerup", pointerUp, true);
        window.addEventListener("pointercancel", pointerCancel, true);
        setBasePlanePanning(true);
        document.body.style.cursor = "grabbing";
        invalidate();
      },
      [
        controlsEnabled,
        editorStore.panning,
        finishBasePlanePan,
        gl.domElement,
        invalidate,
        removeBasePlanePanNativeListeners,
        updateBasePlanePan,
      ],
    );

    const handleBasePlanePointerMove = useCallback(
      (event: ThreeEvent<PointerEvent>) => {
        if (
          !basePlanePanStateRef.current ||
          event.pointerId !== basePlanePanStateRef.current.pointerId
        ) {
          return;
        }

        event.stopPropagation();
        event.nativeEvent.preventDefault();
        event.nativeEvent.stopImmediatePropagation?.();
        updateBasePlanePan(event);
      },
      [updateBasePlanePan],
    );

    const boundsSize = planBounds.isEmpty()
      ? new THREE.Vector2(8, 8)
      : planBounds.getSize(new THREE.Vector2());
    const maxDimension = Math.max(boundsSize.x, boundsSize.y, 4);

    const zoom3DBySteps = useCallback(
      (steps: number): boolean => {
        if (!Number.isFinite(steps) || steps === 0) return false;

        const camera3D = camera3DRef.current;
        const controls = controlsRef.current;
        if (!camera3D || !controls) return false;

        const zoomTarget = getSynchronizedBasePlaneOrbitTarget(
          camera3D,
          controls.target,
        );
        controls.target.copy(zoomTarget);
        orbitTargetRef.current.copy(zoomTarget);

        const offset = camera3D.position.clone().sub(zoomTarget);
        const distance = offset.length();
        if (!Number.isFinite(distance) || distance <= 1e-6) return false;
        const currentQuaternion = camera3D.quaternion.clone();
        const currentUp = camera3D.up.clone();

        const limits = get3DZoomDistanceLimits();
        const minDistance = Math.min(limits.minDistance, distance);
        const maxDistance = Math.max(limits.maxDistance, distance);
        const nextDistance = THREE.MathUtils.clamp(
          distance / Math.pow(THREE_D_ZOOM_STEP_FACTOR, steps),
          minDistance,
          maxDistance,
        );

        if (Math.abs(nextDistance - distance) <= 1e-6) return false;

        userInteracted3DRef.current = true;
        camera3D.position.copy(
          zoomTarget
            .clone()
            .add(offset.normalize().multiplyScalar(nextDistance)),
        );
        camera3D.quaternion.copy(currentQuaternion);
        camera3D.up.copy(currentUp);
        camera3D.updateMatrixWorld();
        markPresetSelectionAsPerspective(editorStore, appliedPresetRef);
        invalidate();
        return true;
      },
      [editorStore, invalidate],
    );

    const reset3DZoom = useCallback((): boolean => {
      const camera3D = camera3DRef.current;
      const controls = controlsRef.current;
      if (!camera3D || !controls) return false;

      const currentTarget = getSynchronizedBasePlaneOrbitTarget(
        camera3D,
        controls.target,
      );
      const focusBounds = editorStore.consume3DResetZoomBounds();
      const boundsToFrame =
        focusBounds && !focusBounds.isEmpty() ? focusBounds : planBounds;
      const resetPose = get3DResetZoomPose(
        editorStore.view3DPreset,
        boundsToFrame,
        camera3D,
        currentTarget,
      );

      userInteracted3DRef.current = true;
      appliedPresetRef.current = editorStore.view3DPreset;
      animateCamera(camera3D, resetPose, 240, invalidate, animationRef, () => {
        controls.target.copy(resetPose.target);
        orbitTargetRef.current.copy(resetPose.target);
        setOrbitTarget(resetPose.target.clone());
        controls.update();
        editorStore.setCamera(camera3D);
        invalidate();
      });
      return true;
    }, [editorStore, editorStore.view3DPreset, invalidate, planBounds]);

    useEffect(() => {
      const requestId = editorStore.view3DZoomRequestId;
      if (
        !active ||
        !controlsReady ||
        requestId === 0 ||
        handled3DZoomRequestIdRef.current === requestId
      ) {
        return;
      }

      handled3DZoomRequestIdRef.current = requestId;
      zoom3DBySteps(editorStore.view3DZoomRequestSteps);
    }, [
      active,
      controlsReady,
      editorStore.view3DZoomRequestId,
      editorStore.view3DZoomRequestSteps,
      zoom3DBySteps,
    ]);

    useEffect(() => {
      const requestId = editorStore.view3DResetZoomRequestId;
      if (
        !active ||
        !controlsReady ||
        requestId === 0 ||
        handled3DResetZoomRequestIdRef.current === requestId
      ) {
        return;
      }

      handled3DResetZoomRequestIdRef.current = requestId;
      reset3DZoom();
    }, [
      active,
      controlsReady,
      editorStore.view3DResetZoomRequestId,
      reset3DZoom,
    ]);

    const syncCurrentOrbitTarget = useCallback(() => {
      const controls = controlsRef.current;
      if (!controls) return;

      orbitTargetRef.current.copy(controls.target);
      invalidate();
    }, [invalidate]);

    useEffect(
      () => () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
        if (controlsReadyTimeoutRef.current !== null) {
          window.clearTimeout(controlsReadyTimeoutRef.current);
          controlsReadyTimeoutRef.current = null;
        }
        if (activeRef.current && savedCameraRef.current) {
          const camera3D = camera3DRef.current;
          const previous2DSnapshot = savedCameraRef.current;
          // If a normal exit started, reuse the snapshot it computed so we
          // don't restore a mid-animation pose. Otherwise (e.g. abrupt
          // unmount with no exit in flight) fall back to building one now.
          const saved =
            pendingExitSnapshotRef.current ??
            buildExit2DSnapshot(
              camera3D ? snapshotCamera(camera3D) : previous2DSnapshot,
              previous2DSnapshot,
              getCurrentOrbitTarget(
                controlsRef.current,
                orbitTargetRef.current,
              ),
              entry3DViewDistanceRef.current,
              userInteracted3DRef.current,
            );
          if (!pendingExitSnapshotRef.current) {
            onExit2DView?.(cameraSnapshotToView(saved));
          }
          restoreCameraSnapshot(saved);
          set({ camera: saved.camera as any });
          editorStore.setCamera(
            saved.camera as THREE.PerspectiveCamera | THREE.OrthographicCamera,
          );
          editorStore.remember2DCameraView(
            saved.camera as THREE.PerspectiveCamera | THREE.OrthographicCamera,
          );
          activeRef.current = false;
          entryRevealActiveRef.current = false;
          savedCameraRef.current = null;
          entry3DViewDistanceRef.current = null;
          appliedPresetRef.current = null;
          entryOrbitTargetRef.current = null;
          pendingExitSnapshotRef.current = null;
        }
      },
      [editorStore, set],
    );

    const basePanHitSize = Math.max(maxDimension * 8, BASE_PAN_MIN_HIT_SIZE);
    const showBasePanHitTarget =
      basePlanePanning || (editorStore.panning && !suppressBasePanHitTarget);
    const controlsShouldBeEnabled = is3DOrbitControlsEnabled({
      active,
      controlsReady,
      controlsEnabled,
      basePlanePanning,
      panning: editorStore.panning,
    });
    controlsEnablementRef.current = {
      active,
      controlsReady,
      controlsEnabled,
      basePlanePanning,
      panning: editorStore.panning,
    };

    const applyLatestControlsEnabledState = useCallback(() => {
      apply3DOrbitControlsEnabledState(
        controls,
        is3DOrbitControlsEnabled(controlsEnablementRef.current),
      );
      invalidate();
    }, [controls, invalidate]);

    useEffect(() => {
      const eventTarget =
        (events.connected as HTMLElement | undefined) ?? gl.domElement;

      if (!active || !controlsReady) {
        controls.dispose();
        return;
      }

      controls.connect(eventTarget);

      const handleControlsStart = () => {
        controlsInteractionActiveRef.current = true;
        userInteracted3DRef.current = true;
        invalidate();
      };
      const handleControlsChange = () => {
        syncCurrentOrbitTarget();
        if (controlsInteractionActiveRef.current) {
          markPresetSelectionAsPerspective(editorStore, appliedPresetRef);
        }
      };
      const handleControlsEnd = () => {
        controlsInteractionActiveRef.current = false;
        syncCurrentOrbitTarget();
      };
      const handleWheel = (event: WheelEvent) => {
        if (!controlsEnabled || basePlanePanning) return;
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        zoom3DBySteps(getWheelZoomSteps(event));
      };
      const restoreControlsAfterExternalGesture = () => {
        window.setTimeout(applyLatestControlsEnabledState, 0);
        window.setTimeout(applyLatestControlsEnabledState, 50);
      };
      const reconcileControlsBeforePointerGesture = (event: PointerEvent) => {
        if (isPointerOverRegisteredTransformControlsPicker(event)) {
          apply3DOrbitControlsEnabledState(controls, false);
          restoreControlsAfterExternalGesture();
          return;
        }
        if (!controlsShouldBeEnabled) return;
        apply3DOrbitControlsEnabledState(controls, true);
      };

      controls.addEventListener("start", handleControlsStart);
      controls.addEventListener("change", handleControlsChange);
      controls.addEventListener("end", handleControlsEnd);
      eventTarget.addEventListener(
        "pointerdown",
        reconcileControlsBeforePointerGesture,
        true,
      );
      window.addEventListener(
        "pointerup",
        restoreControlsAfterExternalGesture,
        true,
      );
      window.addEventListener(
        "pointercancel",
        restoreControlsAfterExternalGesture,
        true,
      );
      window.addEventListener("blur", restoreControlsAfterExternalGesture);
      eventTarget.addEventListener("wheel", handleWheel, {
        passive: false,
        capture: true,
      });
      invalidate();

      return () => {
        controlsInteractionActiveRef.current = false;
        controls.removeEventListener("start", handleControlsStart);
        controls.removeEventListener("change", handleControlsChange);
        controls.removeEventListener("end", handleControlsEnd);
        eventTarget.removeEventListener(
          "pointerdown",
          reconcileControlsBeforePointerGesture,
          true,
        );
        window.removeEventListener(
          "pointerup",
          restoreControlsAfterExternalGesture,
          true,
        );
        window.removeEventListener(
          "pointercancel",
          restoreControlsAfterExternalGesture,
          true,
        );
        window.removeEventListener("blur", restoreControlsAfterExternalGesture);
        eventTarget.removeEventListener("wheel", handleWheel, {
          capture: true,
        });
        controls.dispose();
      };
    }, [
      active,
      basePlanePanning,
      controls,
      controlsReady,
      controlsEnabled,
      editorStore,
      events.connected,
      gl.domElement,
      invalidate,
      applyLatestControlsEnabledState,
      syncCurrentOrbitTarget,
      controlsShouldBeEnabled,
      zoom3DBySteps,
    ]);

    useEffect(() => {
      apply3DOrbitControlsEnabledState(controls, controlsShouldBeEnabled);
      controls.enableZoom = false;
      // Ground-plane pan feels more like moving around a floor plan than
      // screen-space slide, especially with one-finger mobile pan.
      controls.screenSpacePanning = false;
      apply3DOrbitTouchMapping(controls, isMobileUi());
      const { minDistance, maxDistance } = get3DZoomDistanceLimits();
      controls.minDistance = minDistance;
      controls.maxDistance = maxDistance;
      controls.minPolarAngle = 0.001;
      controls.maxPolarAngle = Math.PI - 0.001;
      controls.update();
      invalidate();
    }, [
      active,
      basePlanePanning,
      controls,
      controlsEnabled,
      controlsShouldBeEnabled,
      controlsReady,
      editorStore.panning,
      invalidate,
      suppressBasePanHitTarget,
    ]);

    useFrame(() => {
      if (active && controls.enabled && controls.enableDamping) {
        controls.update();
      }
    }, -1);

    return active && controlsReady ? (
      <>
        {showBasePanHitTarget && (
          <mesh
            position={[cameraTarget.x, cameraTarget.y, BASE_PAN_HIT_PLANE_Z]}
            onPointerDown={handleBasePlanePointerDown}
            onPointerMove={handleBasePlanePointerMove}
            onPointerUp={finishBasePlanePan}
            onPointerCancel={finishBasePlanePan}
            userData={{ floorplannerType: "base-plane-pan-hit-target" }}
          >
            <planeGeometry args={[basePanHitSize, basePanHitSize]} />
            <meshBasicMaterial
              transparent
              opacity={0}
              depthWrite={false}
              side={THREE.DoubleSide}
            />
          </mesh>
        )}
      </>
    ) : null;
  },
);

function getPresetPose(
  preset: FloorPlan3DPreset,
  bounds: THREE.Box2,
): PresetPose {
  const center2 = bounds.isEmpty()
    ? new THREE.Vector2(0, 0)
    : bounds.getCenter(new THREE.Vector2());
  const size = bounds.isEmpty()
    ? new THREE.Vector2(8, 8)
    : bounds.getSize(new THREE.Vector2());
  const maxDimension = Math.max(size.x, size.y, 4);
  const center = new THREE.Vector3(center2.x, center2.y, RESET_ZOOM_TARGET_Z);
  const radius = maxDimension * 1.45 + 3;
  const sideHeight = Math.max(maxDimension * 0.32, 1.5);

  switch (preset) {
    case "birdsEye": {
      const distance = radius + 3;
      const horizontal = Math.sin(BIRDS_EYE_ENTRY_TILT) * distance;
      const elevation = Math.cos(BIRDS_EYE_ENTRY_TILT) * distance;
      return {
        position: new THREE.Vector3(
          center.x,
          center.y - horizontal,
          center.z + elevation,
        ),
        target: center,
        up: new THREE.Vector3(0, 0, 1),
      };
    }
    case "front":
      return {
        position: new THREE.Vector3(center.x, center.y - radius, sideHeight),
        target: center,
      };
    case "back":
      return {
        position: new THREE.Vector3(center.x, center.y + radius, sideHeight),
        target: center,
      };
    case "left":
      return {
        position: new THREE.Vector3(center.x - radius, center.y, sideHeight),
        target: center,
      };
    case "right":
      return {
        position: new THREE.Vector3(center.x + radius, center.y, sideHeight),
        target: center,
      };
    case "dollhouse":
    default: {
      const horizontal = radius * 0.86;
      const elevation = radius * 1.05;
      return {
        position: new THREE.Vector3(
          center.x + horizontal * Math.SQRT1_2,
          center.y - horizontal * Math.SQRT1_2,
          elevation,
        ),
        target: center,
      };
    }
  }
}

export function get3DResetZoomPose(
  preset: FloorPlan3DPreset,
  bounds: THREE.Box2,
  camera: THREE.PerspectiveCamera,
  currentTarget?: THREE.Vector3 | null,
): PresetPose {
  const fallbackPose = getPresetPose(preset, bounds);
  const target = getPlanBoundsCenter3D(bounds);
  const currentDirection =
    currentTarget && isFiniteVector3(currentTarget)
      ? camera.position.clone().sub(currentTarget)
      : new THREE.Vector3();
  const fallbackDirection = fallbackPose.position
    .clone()
    .sub(fallbackPose.target);
  const direction =
    isFiniteVector3(currentDirection) && currentDirection.lengthSq() > 1e-10
      ? currentDirection.normalize()
      : fallbackDirection.normalize();
  const distance = Math.max(
    get3DResetZoomFitDistance(bounds, camera),
    fallbackPose.position.distanceTo(fallbackPose.target),
  );

  return {
    position: target.clone().add(direction.multiplyScalar(distance)),
    target,
    up: camera.up.clone(),
  };
}

function getPlanBoundsCenter3D(bounds: THREE.Box2): THREE.Vector3 {
  const center2 = bounds.isEmpty()
    ? new THREE.Vector2(0, 0)
    : bounds.getCenter(new THREE.Vector2());
  return new THREE.Vector3(center2.x, center2.y, RESET_ZOOM_TARGET_Z);
}

function get3DResetZoomFitDistance(
  bounds: THREE.Box2,
  camera: THREE.PerspectiveCamera,
): number {
  const size = bounds.isEmpty()
    ? new THREE.Vector2(8, 8)
    : bounds.getSize(new THREE.Vector2());
  const halfWidth = Math.max(size.x / 2, 0.5);
  const halfDepth = Math.max(size.y / 2, 0.5);
  const verticalRadius = Math.max(
    RESET_ZOOM_TARGET_Z,
    DEFAULT_WALL_HEIGHT - RESET_ZOOM_TARGET_Z,
    DEFAULT_WALL_HEIGHT / 2,
  );
  const boundingRadius = Math.sqrt(
    halfWidth * halfWidth +
      halfDepth * halfDepth +
      verticalRadius * verticalRadius,
  );
  const fovY = THREE.MathUtils.degToRad(camera.fov);
  const fovX =
    2 * Math.atan(Math.tan(fovY / 2) * Math.max(camera.aspect, 0.001));
  const fitFov = Math.max(Math.min(fovX, fovY), THREE.MathUtils.degToRad(1));
  const distance = boundingRadius / Math.sin(fitFov / 2);

  return Number.isFinite(distance) && distance > 0
    ? distance * RESET_ZOOM_FIT_PADDING
    : MIN_3D_ZOOM_OUT_DISTANCE;
}

function snapshotCamera(camera: THREE.Camera): CameraSnapshot {
  const perspective = camera as THREE.PerspectiveCamera;
  return {
    camera,
    position: camera.position.clone(),
    quaternion: camera.quaternion.clone(),
    up: camera.up.clone(),
    zoom:
      "zoom" in camera
        ? (camera as THREE.PerspectiveCamera | THREE.OrthographicCamera).zoom
        : undefined,
    fov: "fov" in perspective ? perspective.fov : undefined,
  };
}

function createRemembered2DSnapshot(
  camera: THREE.Camera,
  view: { x: number; y: number; z: number; zoom: number },
): CameraSnapshot {
  const snapshot = snapshotCamera(camera);
  const position = new THREE.Vector3(view.x, view.y, view.z);
  const up = new THREE.Vector3(0, 1, 0);
  const quaternion = new THREE.Quaternion().setFromRotationMatrix(
    new THREE.Matrix4().lookAt(
      position,
      new THREE.Vector3(view.x, view.y, 0),
      up,
    ),
  );

  return {
    ...snapshot,
    position,
    quaternion,
    up,
    zoom: view.zoom,
  };
}

function restoreCameraSnapshot(snapshot: CameraSnapshot): void {
  snapshot.camera.position.copy(snapshot.position);
  snapshot.camera.quaternion.copy(snapshot.quaternion);
  snapshot.camera.up.copy(snapshot.up);
  if (snapshot.zoom !== undefined && "zoom" in snapshot.camera) {
    snapshot.camera.zoom = snapshot.zoom;
  }
  if (snapshot.fov !== undefined && "fov" in snapshot.camera) {
    (snapshot.camera as THREE.PerspectiveCamera).fov = snapshot.fov;
  }
  if ("updateProjectionMatrix" in snapshot.camera) {
    (
      snapshot.camera as THREE.PerspectiveCamera | THREE.OrthographicCamera
    ).updateProjectionMatrix();
  }
}

function cameraSnapshotToView(snapshot: CameraSnapshot): {
  x: number;
  y: number;
  z: number;
  zoom: number;
} {
  return {
    x: snapshot.position.x,
    y: snapshot.position.y,
    z: snapshot.position.z,
    zoom: snapshot.zoom ?? 1,
  };
}

function createTopDown2DSnapshot(
  snapshot: CameraSnapshot,
  camera: THREE.Camera = snapshot.camera,
  zoom?: number,
  center?: THREE.Vector3 | THREE.Vector2 | null,
): CameraSnapshot {
  const position = snapshot.position.clone();
  const cameraCenter = getSnapshotTargetOnZPlane(snapshot, 0);
  if (center) {
    position.x = center.x;
    position.y = center.y;
  } else if (cameraCenter) {
    position.x = cameraCenter.x;
    position.y = cameraCenter.y;
  }
  position.z = TWO_D_CAMERA_Z;

  const up = new THREE.Vector3(0, 1, 0);
  const lookAtTarget = new THREE.Vector3(position.x, position.y, 0);
  const quaternion = new THREE.Quaternion().setFromRotationMatrix(
    new THREE.Matrix4().lookAt(position, lookAtTarget, up),
  );

  return {
    ...snapshot,
    camera,
    position,
    quaternion,
    up,
    zoom:
      zoom ??
      (cameraCenter
        ? getEquivalentTopDownZoom(snapshot, cameraCenter)
        : snapshot.zoom),
  };
}

function getCurrentOrbitTarget(
  controls: OrbitControlsImpl | null,
  fallback: THREE.Vector3,
): THREE.Vector3 {
  return (controls?.target ?? fallback).clone();
}

// Builds the 2D snapshot to restore on exit. When the user hasn't interacted
// with the 3D view at all, returns an exact copy of the entry 2D snapshot so
// toggling 2D↔3D is fully lossless (no drift across cycles). Otherwise
// computes a top-down snapshot centered on the current orbit target with
// zoom scaled by view-distance ratio.
function buildExit2DSnapshot(
  current3DSnapshot: CameraSnapshot,
  previous2DSnapshot: CameraSnapshot,
  currentOrbitTarget: THREE.Vector3,
  entry3DViewDistance: number | null,
  userInteracted: boolean,
): CameraSnapshot {
  if (!userInteracted) {
    return {
      camera: previous2DSnapshot.camera,
      position: previous2DSnapshot.position.clone(),
      quaternion: previous2DSnapshot.quaternion.clone(),
      up: previous2DSnapshot.up.clone(),
      zoom: previous2DSnapshot.zoom,
      fov: previous2DSnapshot.fov,
    };
  }

  const restoredZoom = getZoomFrom3DViewDistanceRatio(
    current3DSnapshot,
    previous2DSnapshot.zoom,
    entry3DViewDistance,
  );
  return createTopDown2DSnapshot(
    current3DSnapshot,
    previous2DSnapshot.camera,
    restoredZoom,
    currentOrbitTarget,
  );
}

export function getZoomFrom3DViewDistanceRatio(
  snapshot: CameraSnapshot,
  entryZoom: number | undefined,
  entryDistance: number | null,
): number | undefined {
  if (entryZoom === undefined || entryDistance === null) {
    return undefined;
  }

  const currentDistance = getSnapshotDistanceToZPlane(snapshot, BASE_PLANE_Z);
  if (currentDistance === null) {
    return entryZoom;
  }

  const zoom = entryZoom * (entryDistance / currentDistance);
  return Number.isFinite(zoom) && zoom > 0
    ? Math.max(MIN_INTERACTIVE_ZOOM_LEVEL, zoom)
    : entryZoom;
}

function getEquivalentTopDownZoom(
  snapshot: CameraSnapshot,
  cameraCenter: THREE.Vector3,
): number | undefined {
  if (snapshot.zoom === undefined) return undefined;

  const currentViewDistance = snapshot.position.distanceTo(cameraCenter);
  if (!Number.isFinite(currentViewDistance) || currentViewDistance <= 1e-5) {
    return snapshot.zoom;
  }

  const topDownViewDistance = Math.abs(TWO_D_CAMERA_Z - BASE_PLANE_Z);
  const zoom = (topDownViewDistance * snapshot.zoom) / currentViewDistance;
  return Number.isFinite(zoom) && zoom > 0 ? zoom : snapshot.zoom;
}

function getSnapshotDistanceToZPlane(
  snapshot: CameraSnapshot,
  z: number,
): number | null {
  const target = getSnapshotTargetOnZPlane(snapshot, z);
  if (!target) return null;

  const distance = snapshot.position.distanceTo(target);
  return Number.isFinite(distance) && distance > 1e-5 ? distance : null;
}

function getSnapshotTargetOnZPlane(
  snapshot: CameraSnapshot,
  z: number,
): THREE.Vector3 | null {
  const direction = new THREE.Vector3(0, 0, -1)
    .applyQuaternion(snapshot.quaternion)
    .normalize();
  if (Math.abs(direction.z) < 1e-5) return null;

  const distance = (z - snapshot.position.z) / direction.z;
  if (!Number.isFinite(distance) || distance <= 0) return null;

  return snapshot.position.clone().add(direction.multiplyScalar(distance));
}

export function copyCameraPoseFor3DEntry(
  source: THREE.Camera,
  target: THREE.PerspectiveCamera,
): void {
  target.position.copy(source.position);
  target.quaternion.copy(source.quaternion);
  target.up.copy(source.up);
  // 2D zoom is saved in the 2D snapshot for restoration, but the 3D
  // perspective camera must start from a fixed zoom so apparent wall height
  // and 3D zoom limits do not depend on the 2D zoom level used to enter 3D.
  target.zoom = THREE_D_CAMERA_ZOOM;
  // Orthographic 2D has no fov; keep the historical 2D perspective FOV so
  // 3D entry framing stays consistent with the pre-ortho canvas.
  target.fov =
    "fov" in source && typeof (source as THREE.PerspectiveCamera).fov === "number"
      ? (source as THREE.PerspectiveCamera).fov
      : TWO_D_CAMERA_FOV_DEG;
  target.updateProjectionMatrix();
}

function apply3DEntryPose(
  camera: THREE.PerspectiveCamera,
  sourceSnapshot: CameraSnapshot,
  target: THREE.Vector3,
  preset: FloorPlan3DPreset,
  bounds: THREE.Box2,
): void {
  if (preset !== "birdsEye") return;

  const presetPose = getPresetPose(preset, bounds);
  const direction = presetPose.position.clone().sub(presetPose.target);
  if (direction.lengthSq() < 1e-10) return;

  direction.normalize();
  const distance = get3DEntryViewDistance(sourceSnapshot, direction.z);

  camera.position.copy(target.clone().add(direction.multiplyScalar(distance)));
  camera.up.set(0, 0, 1);
  camera.lookAt(target);
  camera.updateMatrixWorld();
}

export function get3DEntryViewDistance(
  sourceSnapshot: CameraSnapshot,
  directionZ: number,
): number {
  const sourceZoom =
    sourceSnapshot.zoom !== undefined &&
    Number.isFinite(sourceSnapshot.zoom) &&
    sourceSnapshot.zoom > 0
      ? sourceSnapshot.zoom
      : 1;
  const equivalentVerticalDistance = Math.max(
    Math.abs(sourceSnapshot.position.z - BASE_PLANE_Z) / sourceZoom,
    1,
  );

  return equivalentVerticalDistance / Math.max(directionZ, 0.2);
}

export function get2DMinZoomEquivalent3DDistance(): number {
  const distance =
    Math.abs(TWO_D_CAMERA_Z - BASE_PLANE_Z) / MIN_INTERACTIVE_ZOOM_LEVEL;
  return Number.isFinite(distance) && distance > 0
    ? distance
    : MIN_3D_ZOOM_OUT_DISTANCE;
}

export function get3DZoomDistanceLimits(maxDimension?: number): {
  minDistance: number;
  maxDistance: number;
} {
  // Kept as an optional argument for existing callers, but 3D zoom clamps are
  // intentionally viewport-relative rather than total-floorplan-bounds-relative.
  void maxDimension;

  return {
    minDistance: MIN_3D_ZOOM_IN_DISTANCE,
    maxDistance: Math.max(
      MIN_3D_ZOOM_OUT_DISTANCE,
      get2DMinZoomEquivalent3DDistance(),
    ),
  };
}

function create3DEntryOrbitTarget(
  snapshot: CameraSnapshot,
  z: number,
): THREE.Vector3 {
  const basePlaneTarget = getSnapshotTargetOnZPlane(snapshot, BASE_PLANE_Z);
  return new THREE.Vector3(
    basePlaneTarget?.x ?? snapshot.position.x,
    basePlaneTarget?.y ?? snapshot.position.y,
    z,
  );
}

function createRemembered2DOrbitTarget(view: {
  x: number;
  y: number;
}): THREE.Vector3 {
  return new THREE.Vector3(view.x, view.y, BASE_PLANE_Z);
}

function getPointerPointOnBasePlane(
  event: BasePlanePanPointerEvent,
  camera: THREE.Camera,
  element: HTMLElement,
): THREE.Vector3 | null {
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) {
    return null;
  }

  const pointer = new THREE.Vector2(
    ((event.clientX - rect.left) / rect.width) * 2 - 1,
    -((event.clientY - rect.top) / rect.height) * 2 + 1,
  );
  const raycaster = new THREE.Raycaster();
  raycaster.setFromCamera(pointer, camera);
  return raycaster.ray.intersectPlane(BASE_PAN_PLANE, new THREE.Vector3());
}

export function shouldClearSelectionOnBasePanClick(
  event: Pick<ThreeEvent<PointerEvent>, "eventObject" | "intersections">,
): boolean {
  const intersections = event.intersections ?? [];
  const basePanIndex = intersections.findIndex(({ object }) =>
    isBasePanHitTarget(object),
  );

  if (basePanIndex <= 0) {
    return true;
  }

  return !intersections
    .slice(0, basePanIndex)
    .some(({ object }) => hasFloorplannerHitTarget(object));
}

function isBasePanHitTarget(object: THREE.Object3D): boolean {
  return findFloorplannerType(object) === "base-plane-pan-hit-target";
}

function hasFloorplannerHitTarget(object: THREE.Object3D): boolean {
  return Boolean(findFloorplannerType(object));
}

function findFloorplannerType(object: THREE.Object3D): unknown {
  let current: THREE.Object3D | null = object;
  while (current) {
    const type = current.userData?.floorplannerType;
    if (type) return type;
    current = current.parent;
  }
  return undefined;
}

function getWheelZoomSteps(event: WheelEvent): number {
  const deltaModeMultiplier =
    event.deltaMode === 1
      ? 16
      : event.deltaMode === 2
        ? typeof window !== "undefined"
          ? window.innerHeight
          : 800
        : 1;
  const normalizedDeltaY = event.deltaY * deltaModeMultiplier;
  const steps = -normalizedDeltaY / WHEEL_DELTA_PER_ZOOM_STEP;
  return THREE.MathUtils.clamp(
    steps,
    -MAX_WHEEL_ZOOM_STEPS,
    MAX_WHEEL_ZOOM_STEPS,
  );
}

function isFiniteVector3(vector: THREE.Vector3): boolean {
  return (
    Number.isFinite(vector.x) &&
    Number.isFinite(vector.y) &&
    Number.isFinite(vector.z)
  );
}

export function is3DOrbitControlsEnabled({
  active,
  controlsReady,
  controlsEnabled,
  basePlanePanning,
  panning,
}: {
  active: boolean;
  controlsReady: boolean;
  controlsEnabled: boolean;
  basePlanePanning: boolean;
  panning: boolean;
}): boolean {
  return (
    active && controlsReady && controlsEnabled && !basePlanePanning && !panning
  );
}

function apply3DOrbitControlsEnabledState(
  controls: OrbitControlsImpl,
  enabled: boolean,
): void {
  controls.enabled = enabled;
  controls.enablePan = enabled;
  controls.enableRotate = enabled;
}

/**
 * Phone / tablet: one finger pans the plan (move), two fingers orbit.
 * Desktop keeps OrbitControls defaults (one-finger/mouse rotate, two-finger pan).
 */
export function apply3DOrbitTouchMapping(
  controls: OrbitControlsImpl,
  preferOneFingerPan: boolean,
): void {
  controls.touches = preferOneFingerPan
    ? { ONE: THREE.TOUCH.PAN, TWO: THREE.TOUCH.ROTATE }
    : { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN };
}

function markPresetSelectionAsPerspective(
  editorStore: {
    view3DPreset: FloorPlan3DPreset;
    setView3DPreset: (
      preset: FloorPlan3DPreset,
      options?: { animate?: boolean },
    ) => void;
  },
  appliedPresetRef: React.MutableRefObject<FloorPlan3DPreset | null>,
): void {
  appliedPresetRef.current = "dollhouse";
  if (editorStore.view3DPreset !== "dollhouse") {
    editorStore.setView3DPreset("dollhouse", { animate: false });
  }
}

function animateCamera(
  camera: THREE.PerspectiveCamera,
  pose: PresetPose,
  duration: number,
  invalidate: () => void,
  animationRef: React.MutableRefObject<number>,
  onComplete?: () => void,
): void {
  const fromPosition = camera.position.clone();
  const start = performance.now();

  cancelAnimationFrame(animationRef.current);
  const tick = () => {
    const progress = Math.min((performance.now() - start) / duration, 1);
    const eased = easeInOut(progress);
    const up = pose.up ?? new THREE.Vector3(0, 0, 1);
    camera.position.copy(
      getOrbitalCameraPosition(fromPosition, pose.position, pose.target, eased),
    );
    camera.up.copy(up);
    camera.lookAt(pose.target);
    camera.updateMatrixWorld();
    invalidate();
    if (progress < 1) {
      animationRef.current = requestAnimationFrame(tick);
    } else {
      onComplete?.();
    }
  };
  animationRef.current = requestAnimationFrame(tick);
}

function getOrbitalCameraPosition(
  fromPosition: THREE.Vector3,
  toPosition: THREE.Vector3,
  target: THREE.Vector3,
  progress: number,
): THREE.Vector3 {
  const fromOffset = fromPosition.clone().sub(target);
  const toOffset = toPosition.clone().sub(target);
  const fromDistance = fromOffset.length();
  const toDistance = toOffset.length();

  if (fromDistance < 1e-6 || toDistance < 1e-6) {
    return new THREE.Vector3().lerpVectors(fromPosition, toPosition, progress);
  }

  const fromDirection = fromOffset.clone().normalize();
  const toDirection = toOffset.clone().normalize();
  const rotation = new THREE.Quaternion().setFromUnitVectors(
    fromDirection,
    toDirection,
  );
  const partialRotation = new THREE.Quaternion().slerpQuaternions(
    new THREE.Quaternion(),
    rotation,
    progress,
  );
  const distance = THREE.MathUtils.lerp(fromDistance, toDistance, progress);
  const direction = fromDirection.applyQuaternion(partialRotation).normalize();

  return target.clone().add(direction.multiplyScalar(distance));
}

export default FloorPlan3DCameraController;
